import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, notification } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import * as serverconfig from './serverconn';

var CryptoJS = require('crypto-js');

function getSaccoId() {
  if (!localStorage.getItem('sacco')) {
    return '';
  }
  return CryptoJS.AES.decrypt(localStorage.getItem('sacco'), 'my-secret-key@12345').toString(CryptoJS.enc.Utf8);
}

function getBizUserId() {
  if (!localStorage.getItem('bizuserid')) {
    return '';
  }
  return CryptoJS.AES.decrypt(localStorage.getItem('bizuserid'), 'my-secret-key@1234').toString(CryptoJS.enc.Utf8);
}

function appendAuthFields(formData) {
  const userId = getBizUserId();
  if (userId) {
    formData.append('user_id', userId);
  }
  return formData;
}

/** True when the logged-in user is a branch manager. */
export function fetchCurrentUserBranchManager() {
  const userId = getBizUserId();
  if (!userId) {
    return Promise.resolve(false);
  }
  return axios
    .get(`${serverconfig.backendserverurl}/api/accounts/${userId}`)
    .then((res) => res.data.branch_manager === true)
    .catch(() => false);
}

export function fetchUnbalancedJournalsFyAlert(sacco, sampleLimit = 5) {
  const formData = appendAuthFields(new FormData());
  formData.append('sacco', sacco);
  formData.append('sample_limit', String(sampleLimit));
  const token = localStorage.getItem('token') || '';
  return axios.post(
    `${serverconfig.backendserverurl}/customqueries/get_unbalanced_journals_fy_alert`,
    formData,
    {
      timeout: 120000,
      headers: token ? { Authorization: `Token ${token}` } : {},
    }
  );
}

export function UnbalancedJournalsSystemAlert({ refreshKey = 0 }) {
  const [alertData, setAlertData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isBranchManager, setIsBranchManager] = useState(false);

  useEffect(() => {
    fetchCurrentUserBranchManager().then(setIsBranchManager);
  }, []);

  useEffect(() => {
    const sacco = getSaccoId();
    if (!sacco || !isBranchManager) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchUnbalancedJournalsFyAlert(sacco, 5)
      .then((res) => {
        setAlertData(res.data);
      })
      .catch(() => {
        setAlertData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refreshKey, isBranchManager]);

  if (!isBranchManager || loading || dismissed || !alertData || !alertData.has_unbalanced) {
    return null;
  }

  const sampleText =
    alertData.sample_journal_nos && alertData.sample_journal_nos.length > 0
      ? ` Examples: journal #${alertData.sample_journal_nos.slice(0, 5).join(', #')}.`
      : '';

  return (
    <Alert
      type="error"
      showIcon
      closable
      onClose={() => setDismissed(true)}
      style={{ marginBottom: 12 }}
      message={`${alertData.count} unbalanced journal${alertData.count === 1 ? '' : 's'} in the current financial year`}
      description={(
        <span>
          Period checked: <strong>{alertData.dateone}</strong> to <strong>{alertData.datetwo}</strong>
          {alertData.dataentry_date_cap ? (
            <>
              {' '}(from data entry cap <strong>{alertData.dataentry_date_cap}</strong> onwards).
            </>
          ) : (
            '.'
          )}
          {' '}
          Unbalanced entries often come from user mistakes or network interruptions during posting.
          {sampleText}
          {' '}
          <Link
            to={{
              pathname: '/unbalanced_journal_numbers/',
              state: {
                dateone: alertData.dateone,
                datetwo: alertData.datetwo,
              },
            }}
          >
            Open unbalanced journals report
          </Link>
        </span>
      )}
    />
  );
}

export function showUnbalancedJournalsNotification(alertData, isBranchManager = false) {
  if (!isBranchManager || !alertData || !alertData.has_unbalanced) {
    return;
  }

  notification.warning({
    key: 'unbalanced-journals-fy',
    top: 60,
    duration: 0,
    message: 'Unbalanced journal entries detected',
    description: (
      <span>
        {alertData.count} journal{alertData.count === 1 ? '' : 's'} with debits ≠ credits from{' '}
        {alertData.dateone} to {alertData.datetwo}. Open Utilities → Unbalanced journals to review.
      </span>
    ),
    icon: <WarningOutlined style={{ color: '#faad14' }} />,
  });
}

export function fetchTransactionsMissingJournalsFyAlert(sacco, sampleLimit = 5) {
  const formData = appendAuthFields(new FormData());
  formData.append('sacco', sacco);
  formData.append('sample_limit', String(sampleLimit));
  const token = localStorage.getItem('token') || '';
  return axios.post(
    `${serverconfig.backendserverurl}/customqueries/get_transactions_missing_journals_fy_alert`,
    formData,
    {
      timeout: 120000,
      headers: token ? { Authorization: `Token ${token}` } : {},
    }
  );
}

export function TransactionsMissingJournalsSystemAlert({ refreshKey = 0 }) {
  const [alertData, setAlertData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sacco = getSaccoId();
    if (!sacco) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchTransactionsMissingJournalsFyAlert(sacco, 5)
      .then((res) => {
        setAlertData(res.data);
      })
      .catch(() => {
        setAlertData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refreshKey]);

  if (loading || dismissed || !alertData || !alertData.has_missing_journals) {
    return null;
  }

  const sampleText =
    alertData.sample_journal_nos && alertData.sample_journal_nos.length > 0
      ? ` Example journal numbers: #${alertData.sample_journal_nos.slice(0, 5).join(', #')}.`
      : '';

  return (
    <Alert
      type="error"
      showIcon
      closable
      onClose={() => setDismissed(true)}
      style={{ marginBottom: 12 }}
      message={`${alertData.count} member transaction${alertData.count === 1 ? '' : 's'} without GeneralJournal entries`}
      description={(
        <span>
          Period checked: <strong>{alertData.dateone}</strong> to <strong>{alertData.datetwo}</strong>
          {alertData.dataentry_date_cap ? (
            <>
              {' '}(from data entry cap <strong>{alertData.dataentry_date_cap}</strong> onwards).
            </>
          ) : (
            '.'
          )}
          {' '}
          These transactions posted to member subaccounts but have no matching GL journal lines — often caused by network interruptions during save.
          {sampleText}
          {' '}
          <Link
            to={{
              pathname: '/transactions_missing_journals/',
              state: {
                dateone: alertData.dateone,
                datetwo: alertData.datetwo,
              },
            }}
          >
            Open missing journals report
          </Link>
        </span>
      )}
    />
  );
}

export function showTransactionsMissingJournalsNotification(alertData) {
  if (!alertData || !alertData.has_missing_journals) {
    return;
  }

  notification.warning({
    key: 'transactions-missing-journals-fy',
    top: 60,
    duration: 0,
    message: 'Transactions missing GeneralJournal entries',
    description: (
      <span>
        {alertData.count} member transaction{alertData.count === 1 ? '' : 's'} from{' '}
        {alertData.dateone} to {alertData.datetwo} have no GL journal lines for their journal number.
        Open Utilities → Missing GL journals to review.
      </span>
    ),
    icon: <WarningOutlined style={{ color: '#faad14' }} />,
  });
}

export function SystemHealthAlerts() {
  return (
    <>
      <UnbalancedJournalsSystemAlert />
      <TransactionsMissingJournalsSystemAlert />
    </>
  );
}
